import { AbstractControl, ValidationErrors } from '@angular/forms';
import {
  ButtonTriggerWrapperComponent,
  EIremboFormlyFieldTypes,
  EIremboFormlyValidationTypes,
  FormStateService,
  HideFieldWrapperComponent,
  HintFieldWrapperComponent,
  ReadonlyWrapperComponent,
  RfaReadonlyCheckerWrapperComponent,
  formlyConfigs,
  genericUtilValidator,
  CustomFieldStepperComponent,
  CustomExpansionPanelWithIconComponent,
  CustomDynamicRepeaterComponent,
} from '@irembo-andela/irembogov3-common';
import { ConfigOption, FormlyFieldConfig } from '@ngx-formly/core';
import { ValidatorOption } from '@ngx-formly/core/lib/models';
import { debounce, take, interval, map, tap } from 'rxjs';
import { FormlyFieldGroupComponent } from '../../formly-components/formly-types/formly-field-group/formly-field-group.component';
import { FormlyFieldStepperComponent } from '../../formly-components/formly-types/formly-field-stepper/formly-field-stepper.component';
import { CustomServiceSelectorComponent } from '../../formly-components/formly-types/custom-service-selector/custom-service-selector.component';
import { ConditionWrapperComponent } from '../../modules/services/service-configuration/condition-wrapper/condition-wrapper.component';
import { FieldWrapperComponent } from '../../modules/services/service-configuration/field-wrapper/field-wrapper.component';
import { FormlyRepeatComponent } from '../../modules/services/service-configuration/formly-repeat/formly-repeat.component';
import { FormlySwitchComponent } from '../../modules/services/service-configuration/formly-switch/formly-switch.component';
import { PanelWrapperComponent } from '../../modules/services/service-configuration/panel-wrapper/panel-wrapper.component';
import { IrembogovFormlyAttachmentComponent } from 'src/app/modules/services/widgets/irembogov-formly-attachment/irembogov-formly-attachment.component';
import { CustomRichEditorComponent } from 'src/app/modules/services/irembo-workflow-editor/custom-rich-editor/custom-rich-editor.component';
import { CustomMultiselectCheckboxComponent } from '../../modules/services/service-configuration/custom-multiselect-checkbox/custom-multiselect-checkbox.component';
import { FormlyFieldStepperSectionComponent } from '../../formly-components/formly-types/formly-field-stepper-section/formly-field-stepper-section.component';
import { FormlyFieldHtmlEditorComponent } from '../../formly-components/formly-types/formly-field-html-editor/formly-field-html-editor.component';
import { ServiceValidationRuleWrapperComponent } from '../../modules/services/service-configuration/service-validation-rule-wrapper/service-validation-rule-wrapper.component';
import { PaginatedTemplateDropdownComponent } from '../../formly-components/formly-types/paginated-template-dropdown/paginated-template-dropdown.component';

export function formlyConfigFactory(formStateService: FormStateService) {
  const configsObj: ConfigOption = { ...formlyConfigs() };

  const asyncFormStateUtilValidation: ValidatorOption = {
    name: EIremboFormlyValidationTypes.asyncFormStateUtilValidator,
    validation: (
      control: AbstractControl,
      field: FormlyFieldConfig,
      options: Record<string, string> = {}
    ) => {
      return formStateService.getFormlyFormStateSubject().pipe(
        debounce(() => interval(500)),
        tap(() => field.formControl?.markAsTouched()),
        map(() => genericValidationMapper(control, field, options)),
        take(1)
      );
    },
  };

  configsObj.validators?.push(asyncFormStateUtilValidation);

  return {
    ...configsObj,
    wrappers: [
      {
        name: 'field-wrapper',
        component: FieldWrapperComponent,
        types: [
          EIremboFormlyFieldTypes.input,
          EIremboFormlyFieldTypes.textarea,
          EIremboFormlyFieldTypes.checkbox,
          EIremboFormlyFieldTypes.multicheckbox,
          EIremboFormlyFieldTypes.radio,
          ...formlyConfigs().types.map(type => type.name),
        ],
      },
      {
        name: 'hidden-field-wrapper',
        component: HideFieldWrapperComponent,
        types: [
          EIremboFormlyFieldTypes.input,
          EIremboFormlyFieldTypes.textarea,
          EIremboFormlyFieldTypes.checkbox,
          EIremboFormlyFieldTypes.multicheckbox,
          EIremboFormlyFieldTypes.radio,
          EIremboFormlyFieldTypes.sections,
          EIremboFormlyFieldTypes['formly-group'],
          EIremboFormlyFieldTypes.block,
          ...formlyConfigs().types.map(type => type.name),
        ],
      },
      { name: 'condition-wrapper', component: ConditionWrapperComponent },
      { name: 'panel-wrapper', component: PanelWrapperComponent },
      {
        name: 'hint-field-wrapper',
        component: HintFieldWrapperComponent,
        types: [
          'input',
          'textarea',
          'multicheckbox',
          'block',
          'formly-group',
          'sections',
          'radio',
          ...formlyConfigs().types.map(type => type.name),
        ],
      },
      {
        name: 'rfa-readonly-field-wrapper',
        component: RfaReadonlyCheckerWrapperComponent,
        types: [
          'input',
          'textarea',
          'multicheckbox',
          'radio',
          ...formlyConfigs()
            .types.map(type => type.name)
            .filter(
              name =>
                ![
                  EIremboFormlyFieldTypes.customrepeater,
                  EIremboFormlyFieldTypes.customsectionmessagewithlink,
                ].includes(name)
            ),
        ],
      },
      {
        name: 'irembogov-readonly-wrapper',
        component: ReadonlyWrapperComponent,
        types: [
          'input',
          'textarea',
          'multicheckbox',
          'radio',
          ...formlyConfigs().types.map(type => type.name),
        ],
      },
      {
        name: 'irembogov-button-trigger-wrapper',
        component: ButtonTriggerWrapperComponent,
        types: ['customgenericdatafetch'],
      },
      {
        name: 'custom-expansion-panel-with-icon',
        component: CustomExpansionPanelWithIconComponent,
      },
      {
        name: 'service-validation-rule-wrapper',
        component: ServiceValidationRuleWrapperComponent,
      },
    ],
    ...{
      types: [
        ...formlyConfigs().types.map(type => ({
          component: type.component,
          name: type.name,
          wrappers: ['form-field'],
        })),
        { name: 'block', component: FormlyFieldGroupComponent, wrappers: [] },
        {
          name: 'formly-group',
          component: FormlyFieldStepperSectionComponent,
          wrappers: [],
        },
        {
          name: 'sections',
          component: FormlyFieldStepperComponent,
          wrappers: [],
        },
        { name: 'formlyswitch', component: FormlySwitchComponent },
        { name: 'repeat', component: FormlyRepeatComponent },
        {
          name: 'formly-file-input',
          component: IrembogovFormlyAttachmentComponent,
        },
        { name: 'tabs', component: CustomFieldStepperComponent },
        { name: 'customricheditor', component: CustomRichEditorComponent },
        {
          name: 'customdropdownmultipleselector',
          component: CustomMultiselectCheckboxComponent,
        },
        {
          name: 'customserviceselector',
          component: CustomServiceSelectorComponent,
        },
        {
          name: 'customdynamicrepeater',
          component: CustomDynamicRepeaterComponent,
          wrappers: [],
        },
        {
          name: 'htmleditor',
          component: FormlyFieldHtmlEditorComponent,
          wrappers: ['field-wrapper'],
        },
        {
          name: 'customdropdownpaginatedwithtemplate',
          component: PaginatedTemplateDropdownComponent,
          wrappers: ['field-wrapper'],
        },
      ],
    },
  };
}

function genericValidationMapper(
  control: AbstractControl,
  field: FormlyFieldConfig,
  options: Record<string, string> = {}
): ValidationErrors | null {
  return genericUtilValidator(control, field, options);
}
